import * as Application from 'expo-application';
import * as Device from 'expo-device';
import { Platform } from 'react-native';
import nacl from 'tweetnacl';
import naclUtil from 'tweetnacl-util';

export type ClientInstallationMetadata = {
  schema_version: 1;
  brand?: string;
  manufacturer?: string;
  model?: string;
  os_name?: string;
  os_version?: string;
  os_build?: string;
  is_device?: boolean;
  installed_at?: string;
  last_updated_at?: string;
  browser?: string;
  browser_version?: string;
  display_mode?: 'browser' | 'standalone';
  correlation_source?: 'android_id' | 'ios_idfv';
  correlation_hash?: string;
  company_correlation_hash_v1?: string;
};

const text = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim().slice(0, 128) : undefined;

// Uma API opcional indisponível não pode segurar a fila de presença.
const optional = async <T>(read: () => T | Promise<T>): Promise<T | null> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      Promise.resolve().then(read),
      new Promise<null>(resolve => { timer = setTimeout(() => resolve(null), 1_000); }),
    ]);
  } catch {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
};

const date = (value: Date | null): string | undefined =>
  value instanceof Date && Number.isFinite(value.getTime()) ? value.toISOString() : undefined;

export const parseBrowserMetadata = (ua: string): Pick<ClientInstallationMetadata,
  'browser' | 'browser_version' | 'os_name'> => {
  const browser = [
    ['Edge', /(?:Edg|EdgA|EdgiOS)\/([\d.]+)/],
    ['Opera', /(?:OPR|OPiOS)\/([\d.]+)/],
    ['Samsung Internet', /SamsungBrowser\/([\d.]+)/],
    ['Firefox', /(?:Firefox|FxiOS)\/([\d.]+)/],
    ['Chrome', /(?:Chrome|CriOS)\/([\d.]+)/],
    ['Safari', /Version\/([\d.]+).*Safari/],
  ] as const;
  const match = browser.map(([name, pattern]) => ({ name, match: ua.match(pattern) })).find(item => item.match);
  // O User-Agent pode congelar a versão do sistema; coletar apenas sua família.
  const os = /Android/.test(ua) ? 'Android' : /(?:iPhone|iPad|CPU OS)/.test(ua) ? 'iOS'
    : /Windows NT/.test(ua) ? 'Windows'
      : /CrOS/.test(ua) ? 'ChromeOS'
        : /Mac OS X/.test(ua) ? 'macOS'
          : /Linux/.test(ua) ? 'Linux' : undefined;
  return { browser: match?.name, browser_version: text(match?.match?.[1]), os_name: os };
};

export const collectClientInstallationMetadata = async (scope: {
  companyId: string;
  userId: string;
}): Promise<ClientInstallationMetadata> => {
  if (Platform.OS === 'web') {
    const nav = typeof navigator === 'undefined' ? undefined : navigator as Navigator & { standalone?: boolean };
    const standalone = nav?.standalone === true
      || (typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)').matches === true);
    return { schema_version: 1, ...parseBrowserMetadata(nav?.userAgent ?? ''), display_mode: standalone ? 'standalone' : 'browser' };
  }

  const source = Platform.OS === 'android' ? 'android_id' : 'ios_idfv';
  const [nativeId, installedAt, updatedAt] = await Promise.all([
    optional(() => Platform.OS === 'android' ? Application.getAndroidId() : Application.getIosIdForVendorAsync()),
    optional(() => Application.getInstallationTimeAsync()),
    Platform.OS === 'android' ? optional(() => Application.getLastUpdateTimeAsync()) : Promise.resolve(null),
  ]);
  const normalizedNativeId = text(nativeId);
  const hash = (parts: string[]) => Array.from(nacl.hash(naclUtil.decodeUTF8(JSON.stringify(parts))))
    .map(byte => byte.toString(16).padStart(2, '0')).join('');
  // Mantém a chave legada por conta para clientes/painel existentes.
  const correlation = normalizedNativeId && Device.isDevice
    ? hash(['app-guincho-installation-v1', scope.companyId, scope.userId, source, nativeId as string])
    : undefined;
  // A chave nova normaliza o ID, inclui empresa/plataforma/fonte e nunca o envia em claro.
  const companyCorrelation = normalizedNativeId && Device.isDevice
    ? hash(['app-guincho-company-installation-v1', scope.companyId, Platform.OS, source, normalizedNativeId])
    : undefined;
  // No Mac, UIKit pode declarar modelo/versão de iPad; isso não descreve o hospedeiro.
  const iosOnMac = Platform.OS === 'ios' && Device.deviceType === Device.DeviceType.DESKTOP;
  return {
    schema_version: 1,
    brand: text(Device.brand), manufacturer: text(Device.manufacturer),
    model: iosOnMac ? 'Mac (app iOS)' : text(Device.modelName),
    os_name: Platform.OS === 'android' ? 'Android' : iosOnMac ? 'macOS' : text(Device.osName),
    os_version: iosOnMac ? undefined : text(Device.osVersion), os_build: iosOnMac ? undefined : text(Device.osBuildId),
    is_device: Device.isDevice, installed_at: date(installedAt), last_updated_at: date(updatedAt),
    correlation_source: correlation ? source : undefined, correlation_hash: correlation,
    company_correlation_hash_v1: companyCorrelation,
  };
};
